import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { fetchAll } from '../lib/fetchAll'
import { Icon, Avatar, StatusPill, Spinner, EmptyState, STATUS, STATUS_ORDER, normStatus, timeAgo, fmtDateTime, requesterName } from './ui'

const TABS = [
  { key: 'tutti', label: 'Tutti' },
  { key: 'aperto', label: 'Aperti' },
  { key: 'in_lavorazione', label: 'In lavorazione' },
  { key: 'risolto', label: 'Risolti' },
]

const SORTS = {
  recenti:    { label: 'Più recenti',            fn: (a, b) => new Date(b.created_at) - new Date(a.created_at) },
  aggiornati: { label: 'Aggiornati di recente',  fn: (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at) },
  vecchi:     { label: 'Meno recenti',           fn: (a, b) => new Date(a.created_at) - new Date(b.created_at) },
  numero:     { label: 'Numero ticket',          fn: (a, b) => (b.number || 0) - (a.number || 0) },
}

const snippet = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, 160)

export default function TicketList({ profile, openTicket, setView, scope = 'all', status: fixedStatus = null, title = 'Ticket', subtitle }) {
  const [tickets, setTickets] = useState([])
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState('tutti')
  const [sort, setSort] = useState('recenti')
  const [deepSearch, setDeepSearch] = useState(false)
  const [msgMatches, setMsgMatches] = useState(null)   // ticket con la parola cercata nei messaggi
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadTickets() }, [])

  // "Cerca nei messaggi": la ricerca la fa il database (prima si scaricavano tutti i messaggi,
  // ma Supabase ne restituisce al massimo 1000 e gli altri restavano esclusi)
  useEffect(() => {
    const q = search.trim()
    if (!deepSearch || q.length < 2) { setMsgMatches(null); return }
    let alive = true
    const timer = setTimeout(async () => {
      const { data } = await fetchAll(() => supabase.from('comments').select('ticket_id').ilike('content', `%${q}%`).order('id'))
      if (alive) setMsgMatches(new Set((data || []).map(r => r.ticket_id)))
    }, 300)
    return () => { alive = false; clearTimeout(timer) }
  }, [deepSearch, search])

  const loadTickets = async () => {
    setLoading(true)
    const { data } = await fetchAll(() => supabase.from('tickets')
      .select('*, created_by_profile:profiles!tickets_created_by_fkey(name), assigned_to_profile:profiles!tickets_assigned_to_fkey(name)')
      .order('created_at', { ascending: false }).order('id'))
    setTickets(data || [])
    setLoading(false)
  }

  // Ambito della vista
  const scoped = tickets.filter(t => {
    const st = normStatus(t.status)
    if (scope === 'mine' && t.assigned_to !== profile?.id) return false
    if (scope === 'unassigned' && (t.assigned_to || st === 'risolto')) return false
    if (fixedStatus && st !== fixedStatus) return false
    return true
  })

  // Ricerca (titolo, riferimento interno, descrizione, email, numero e — se attivo — testo dei messaggi)
  const term = search.trim().toLowerCase()
  const numTerm = term.replace(/^#/, '')
  const searched = scoped.filter(t => !term
    || t.title.toLowerCase().includes(term)
    || t.internal_ref?.toLowerCase().includes(term)
    || t.description?.toLowerCase().includes(term)
    || t.recipient_email?.toLowerCase().includes(term)
    || (numTerm && String(t.number ?? '') === numTerm)
    || (deepSearch && !!msgMatches?.has(t.id)))

  const tabCount = (k) => k === 'tutti' ? searched.length : searched.filter(t => normStatus(t.status) === k).length
  const visible = (fixedStatus || tab === 'tutti' ? searched : searched.filter(t => normStatus(t.status) === tab))
    .slice().sort(SORTS[sort].fn)

  // Raggruppamento per stato quando si vedono tutti gli stati insieme
  const grouped = !fixedStatus && tab === 'tutti'
  const groups = grouped
    ? STATUS_ORDER.map(s => ({ status: s, items: visible.filter(t => normStatus(t.status) === s) })).filter(g => g.items.length)
    : [{ status: null, items: visible }]

  const renderRow = (t) => {
    const s = STATUS[normStatus(t.status)]
    const fromEmail = !!t.recipient_email
    return (
      <tr key={t.id} className="trow" onClick={() => openTicket(t)}>
        <td className="c-status">
          <span className="sbar" style={{ background: s.dot }} />
          <StatusPill status={t.status} size="sm" />
        </td>
        <td className="c-subj">
          <div className="subj">
            {t.number != null && <span className="subj-num">#{t.number}</span>}
            <span className="subj-title truncate">{t.title}</span>
          </div>
          {(t.internal_ref || t.description) && (
            <div className="subj-snip subj-line2">
              {t.internal_ref && <span className="ref-chip" title={`Riferimento interno: ${t.internal_ref}`}><Icon name="lock" size={10} /> {t.internal_ref}</span>}
              {t.description && <span className="truncate">{snippet(t.description)}</span>}
            </div>
          )}
        </td>
        <td className="c-req">
          {fromEmail ? (
            <div className="who">
              <Avatar name={t.recipient_email} size={26} />
              <div className="minw0">
                <div className="truncate" style={{ fontWeight: 500 }}>{requesterName(t.recipient_email)}</div>
                <div className="who-sub truncate">{t.recipient_email}</div>
              </div>
            </div>
          ) : (
            <div className="who">
              <Avatar name={t.created_by_profile?.name || 'Interno'} size={26} />
              <div className="minw0">
                <div className="truncate" style={{ fontWeight: 500 }}>{t.created_by_profile?.name || '—'}</div>
                <div className="who-sub">Ticket interno</div>
              </div>
            </div>
          )}
        </td>
        <td className="c-asg">
          {t.assigned_to_profile
            ? <div className="who"><Avatar name={t.assigned_to_profile.name} size={24} /><span className="truncate">{t.assigned_to_profile.name}</span></div>
            : <span className="unassigned">Non assegnato</span>}
        </td>
        <td className="c-time" title={`Creato ${fmtDateTime(t.created_at)}`}>{timeAgo(t.created_at)}</td>
      </tr>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-sub">{subtitle}</p>}
        </div>
        <button className="btn btn-primary" onClick={() => setView('new')}><Icon name="plus" size={16} strokeWidth={2.5} /> Nuovo ticket</button>
      </div>

      <div className="card">
        {!fixedStatus && (
          <div className="tabs">
            {TABS.map(tb => (
              <button key={tb.key} className={`tab${tab === tb.key ? ' active' : ''}`} onClick={() => setTab(tb.key)}>
                {tb.key !== 'tutti' && <span className="pill-dot" style={{ background: STATUS[tb.key].dot }} />}
                {tb.label}
                <span className="badge">{tabCount(tb.key)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="toolbar">
          <div className="search">
            <Icon name="search" size={16} className="search-ico" />
            <input className="input input-sm" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per oggetto, riferimento, email, numero…" />
          </div>
          <label className="check" title="Estende la ricerca al testo dei messaggi del ticket">
            <input type="checkbox" checked={deepSearch} onChange={e => setDeepSearch(e.target.checked)} />
            Cerca nei messaggi
          </label>
          <div className="row" style={{ gap: 6 }}>
            <Icon name="sort" size={15} className="faint" />
            <select className="select select-sm" style={{ width: 'auto' }} value={sort} onChange={e => setSort(e.target.value)}>
              {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <span className="toolbar-count">{visible.length} {visible.length === 1 ? 'ticket' : 'ticket'}</span>
        </div>

        {loading ? (
          <div className="empty"><Spinner size={22} /></div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={term ? 'search' : 'inbox'}
            title={term ? 'Nessun risultato' : 'Nessun ticket qui'}
            text={term ? 'Prova con altre parole o attiva "Cerca nei messaggi".' : 'Quando arriveranno nuove richieste le troverai in questa vista.'}
          />
        ) : (
          <table className="tbl">
            <colgroup>
              <col style={{ width: 160 }} />
              <col />
              <col className="c-req" style={{ width: 250 }} />
              <col className="c-asg" style={{ width: 190 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Stato</th>
                <th>Oggetto</th>
                <th className="c-req">Richiedente</th>
                <th className="c-asg">Assegnatario</th>
                <th>Creato</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => [
                g.status && (
                  <tr key={`g-${g.status}`} className="group-row">
                    <td colSpan={5}>
                      <div className="row">
                        <span className="pill-dot" style={{ background: STATUS[g.status].dot }} />
                        {STATUS[g.status].label}
                        <span className="badge">{g.items.length}</span>
                      </div>
                    </td>
                  </tr>
                ),
                ...g.items.map(renderRow)
              ])}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
